import { DefaultError, MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useLoginContext } from '@tiffinger-thiel/appauth-react';
import axios from 'axios';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import {
  ATLunchApi,
  ATMobilityApi,
  BAVApi,
  BenefitsApi,
  BikeApi,
  BikeleasingApi,
  BikeleasingV2Api,
  CarApi,
  Configuration,
  CouponsApi,
  CouponsV2Api,
  CreditCardApi,
  CustomBenefitApi,
  DiscountCodeApi,
  DocumentsApi,
  EmployeeApi,
  FitnessApi,
  HRSystemApi,
  InternetApi,
  ITLeasingApi,
  KitaApi,
  KomboApi,
  LegalTermsApi,
  LunchApi,
  MeApi,
  MobilityApi,
  NewsApi,
  PublicApi,
  ReceiptAuditApi,
  ReceiptExportApi,
  RecreationApi,
  SalaryCalculatorApi,
  SepaApi,
  TenantApi,
  UserApi,
  UserMarketingApi,
  WellpassApi,
} from '../../api';
import { useWithMessage } from '../error/MessageError';
import { getTokenSubClaim } from './parseToken';

export interface APIs {
  me: MeApi;
  user: UserApi;
  benefits: BenefitsApi;
  employee: EmployeeApi;
  discountCode: DiscountCodeApi;
  tenants: TenantApi;
  atLunch: ATLunchApi;
  atMobility: ATMobilityApi;
  lunch: LunchApi;
  mobility: MobilityApi;
  internet: InternetApi;
  news: NewsApi;
  recreation: RecreationApi;
  salaryCalculator: SalaryCalculatorApi;
  coupons: CouponsApi;
  couponsV2: CouponsV2Api;
  customBenefits: CustomBenefitApi;
  receiptAudit: ReceiptAuditApi;
  receiptExport: ReceiptExportApi;
  sepa: SepaApi;
  hrSystem: HRSystemApi;
  documents: DocumentsApi;
  bav: BAVApi;
  bike: BikeApi;
  legalTerms: LegalTermsApi;
  bikeLeasing: BikeleasingApi;
  bikeLeasingV2: BikeleasingV2Api;
  fitness: FitnessApi;
  kita: KitaApi;
  kombo: KomboApi;
  creditCard: CreditCardApi;
  car: CarApi;
  public: PublicApi;
  itLeasing: ITLeasingApi;
  userMarketing: UserMarketingApi;
  wellpass: WellpassApi;

  /**
   * Injects a new config for the apis.
   */
  setAPIConfig: (conf: Configuration) => void;
}

const initialConfig = new Configuration({
  basePath: (import.meta.env.VITE_APP_API_URL || '').toString(),
});

export const apis: APIs = {
  me: new MeApi(initialConfig),
  user: new UserApi(initialConfig),
  employee: new EmployeeApi(initialConfig),
  discountCode: new DiscountCodeApi(initialConfig),
  tenants: new TenantApi(initialConfig),
  benefits: new BenefitsApi(initialConfig),
  atLunch: new ATLunchApi(initialConfig),
  atMobility: new ATMobilityApi(initialConfig),
  lunch: new LunchApi(initialConfig),
  mobility: new MobilityApi(initialConfig),
  internet: new InternetApi(initialConfig),
  news: new NewsApi(initialConfig),
  recreation: new RecreationApi(initialConfig),
  salaryCalculator: new SalaryCalculatorApi(initialConfig),
  coupons: new CouponsApi(initialConfig),
  couponsV2: new CouponsV2Api(initialConfig),
  customBenefits: new CustomBenefitApi(initialConfig),
  receiptAudit: new ReceiptAuditApi(initialConfig),
  receiptExport: new ReceiptExportApi(initialConfig),
  sepa: new SepaApi(initialConfig),
  hrSystem: new HRSystemApi(initialConfig),
  documents: new DocumentsApi(initialConfig),
  bav: new BAVApi(initialConfig),
  bike: new BikeApi(initialConfig),
  legalTerms: new LegalTermsApi(initialConfig),
  bikeLeasing: new BikeleasingApi(initialConfig),
  bikeLeasingV2: new BikeleasingV2Api(initialConfig),
  fitness: new FitnessApi(initialConfig),
  kita: new KitaApi(initialConfig),
  kombo: new KomboApi(initialConfig),
  creditCard: new CreditCardApi(initialConfig),
  car: new CarApi(initialConfig),
  public: new PublicApi(initialConfig),
  itLeasing: new ITLeasingApi(initialConfig),
  userMarketing: new UserMarketingApi(initialConfig),
  wellpass: new WellpassApi(initialConfig),
  setAPIConfig: config => {
    apis.me = new MeApi(config);
    apis.user = new UserApi(config);
    apis.employee = new EmployeeApi(config);
    apis.discountCode = new DiscountCodeApi(config);
    apis.tenants = new TenantApi(config);
    apis.benefits = new BenefitsApi(config);
    apis.atLunch = new ATLunchApi(config);
    apis.atMobility = new ATMobilityApi(config);
    apis.lunch = new LunchApi(config);
    apis.mobility = new MobilityApi(config);
    apis.coupons = new CouponsApi(config);
    apis.couponsV2 = new CouponsV2Api(config);
    apis.internet = new InternetApi(config);
    apis.news = new NewsApi(config);
    apis.recreation = new RecreationApi(config);
    apis.salaryCalculator = new SalaryCalculatorApi(config);
    apis.customBenefits = new CustomBenefitApi(config);
    apis.receiptAudit = new ReceiptAuditApi(config);
    apis.receiptExport = new ReceiptExportApi(config);
    apis.sepa = new SepaApi(config);
    apis.hrSystem = new HRSystemApi(config);
    apis.documents = new DocumentsApi(config);
    apis.bav = new BAVApi(config);
    apis.bike = new BikeApi(config);
    apis.legalTerms = new LegalTermsApi(config);
    apis.bikeLeasing = new BikeleasingApi(config);
    apis.bikeLeasingV2 = new BikeleasingV2Api(config);
    apis.public = new PublicApi(config);
    apis.fitness = new FitnessApi(config);
    apis.kita = new KitaApi(config);
    apis.creditCard = new CreditCardApi(config);
    apis.kombo = new KomboApi(config);
    apis.car = new CarApi(config);
    apis.itLeasing = new ITLeasingApi(config);
    apis.userMarketing = new UserMarketingApi(config);
    apis.wellpass = new WellpassApi(config);
  },
};

interface Props {
  children: React.ReactNode;
  onLoginStateChange?: (hasToken: boolean) => void;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      skipGlobalErrorHandler?: boolean;
    };
  }
}

let handleError: (error: DefaultError) => void = () => {};
// mutations with their own onError are expected to display the error themselves (e.g. via useWithMessage)
const mutationCache = new MutationCache({
  onError: (err, _variables, _context, mutation) => {
    if (!mutation.options.onError && !mutation.options.meta?.skipGlobalErrorHandler) {
      handleError(err);
    }
  },
});
const queryCache = new QueryCache({ onError: err => handleError(err) });
const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const APILoader: FC<Props> = ({ onLoginStateChange, children }) => {
  const withMessage = useWithMessage();

  useEffect(() => {
    handleError = (error: DefaultError) => {
      withMessage(error);
    };
  }, [withMessage]);

  const { token, checkToken } = useLoginContext();
  const tokenSub = useMemo(() => getTokenSubClaim(token), [token]);
  const previousTokenSub = useRef<string | undefined>(tokenSub);

  // Instead of a useEffect, pass the token to the global API instance in a useMemo call
  // that runs synchronously in the render phase. This avoids race conditions with child components
  // listening for changes to isLoggedIn
  useMemo(() => {
    apis.setAPIConfig(
      new Configuration({
        basePath: (import.meta.env.VITE_APP_API_URL || '').toString(),
        baseOptions: token
          ? {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            }
          : undefined,
      }),
    );
  }, [token]);

  useEffect(() => {
    if (previousTokenSub.current === tokenSub) {
      return;
    }

    previousTokenSub.current = tokenSub;
    onLoginStateChange?.(!!token);
  }, [onLoginStateChange, tokenSub, token]);

  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(async config => {
      const result = await checkToken();
      if (result) {
        config.headers.setAuthorization('Bearer ' + result.token);
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [checkToken]);

  return (
    <QueryClientProvider client={queryClient}>
      {children} <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
